import { deleteUnit } from '@/api/units';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { Avatar, Box, Button, Card, CardHeader, Chip, Divider, IconButton, Stack, Typography } from '@mui/material';
import {
  BuildingApartment as BuildingApartmentIcon,
  PencilSimple as PencilSimpleIcon,
  Trash as TrashIcon,
} from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { useConfirm } from 'material-ui-confirm';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import type { Unit } from '@/types/v2/unit';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';

import { EditUnitDialog } from './edit-unit-dialog';

interface UnitDetailsProps {
  unit: Unit;
  onRefresh?: () => void;
}

export function UnitDetails({ unit, onRefresh }: UnitDetailsProps): React.JSX.Element {
  const editDialog = useDialog();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const deleteUnitMutation = useMutation({
    mutationFn: (id: string) => deleteUnit(id),
    onSuccess: () => {
      toast.success('Unit deleted successfully');
      navigate(paths.units.index);
    },
    onError: (error: any) => {
      console.error('Error deleting unit:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete unit');
    },
  });

  const handleDeleteUnit = () => {
    confirm({
      title: 'Delete Unit',
      description: (
        <Box>
          <Typography sx={{ mb: 2 }} variant="body2">
            Are you sure you want to delete this unit? This action cannot be undone.
          </Typography>
          <Typography color="error.main" sx={{ fontWeight: 600, mb: 1 }} variant="body2">
            ⚠️ Risks and Consequences:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <Typography color="error.main" component="li" variant="body2">
              All unit assignments will be permanently removed
            </Typography>
            <Typography color="error.main" component="li" variant="body2">
              Unit history and data will be lost
            </Typography>
            <Typography color="error.main" component="li" variant="body2">
              Any active requests or maintenance records will be affected
            </Typography>
          </Box>
          <Typography color="warning.main" sx={{ fontWeight: 600 }} variant="body2">
            Please ensure all residents have been moved out and all outstanding issues are resolved.
          </Typography>
        </Box>
      ),
      confirmationText: 'Delete Unit',
      cancellationText: 'Cancel',
      confirmationButtonProps: {
        color: 'error',
        variant: 'contained',
      },
      cancellationButtonProps: {
        variant: 'outlined',
      },
    })
      .then(async () => {
        await deleteUnitMutation.mutateAsync(unit.id.toString());
      })
      .catch(() => {
        // User cancelled
      });
  };

  return (
    <Stack spacing={4}>
      <Card>
        <CardHeader
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => {
                  editDialog.handleOpen();
                }}
              >
                <PencilSimpleIcon />
              </IconButton>
              <RoleGuard
                requiredAccountRoles={[
                  ACCOUNT_ROLES.ACCOUNT_OWNER,
                  ACCOUNT_ROLES.ACCOUNT_ADMIN,
                  ACCOUNT_ROLES.ACCOUNT_MANAGER,
                ]}
                requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
              >
                <Button
                  color="error"
                  disabled={deleteUnitMutation.isPending}
                  onClick={handleDeleteUnit}
                  size="small"
                  startIcon={<TrashIcon />}
                  sx={{ minWidth: 'auto', px: 2 }}
                  variant="outlined"
                >
                  Delete
                </Button>
              </RoleGuard>
            </Box>
          }
          avatar={
            <Avatar>
              <BuildingApartmentIcon fontSize="var(--Icon-fontSize)" />
            </Avatar>
          }
          title="Unit Details"
        />
        <PropertyList divider={<Divider />} orientation="vertical" sx={{ '--PropertyItem-padding': '12px 24px' }}>
          {(
            [
              { key: 'ID', value: <Chip label={unit.id} size="small" variant="soft" /> },
              { key: 'Address', value: unit.address },
              { key: 'Unit Number', value: unit.unit_number },
              ...(unit.floor ? [{ key: 'Floor', value: unit.floor }] : []),
              {
                key: 'Location',
                value: `${unit.address} ${unit.city}, ${unit.state} ${unit.zip_code}`,
              },
            ] satisfies { key: string; value: React.ReactNode }[]
          ).map(
            (item): React.JSX.Element => (
              <PropertyItem key={item.key} name={item.key} value={item.value} />
            )
          )}
        </PropertyList>
      </Card>

      {/* Edit Unit Dialog */}
      <EditUnitDialog
        onClose={editDialog.handleClose}
        onSuccess={() => {
          if (onRefresh) {
            onRefresh();
          }
        }}
        open={editDialog.open}
        unit={unit}
      />
    </Stack>
  );
}
